import { IonContent, IonPage } from '@ionic/react';
import { useClerk, useUser } from '../auth/useAuth';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import pkg from '../../package.json';
import { useIsAdmin } from '../api/admin';
import type { GameId } from '../api/types';
import { Btn, Icon } from '../components/chancey';
import { safeAreaPadding, safeAreaPaddingX, withSafeArea } from '../components/layout/safeArea';
import { usePaywall } from '../components/subscription/usePaywall';
import { GAME_LIST, type GameDefinition } from '../data/games';
import { useActiveGame } from '../hooks/useActiveGame';
import { useChanceyTheme } from '../theme/ThemeContext';
import { useUserPrefs } from '../hooks/useUserPrefs';
import { NOTIFICATION_PREF_ROWS, type NotificationPrefKey } from '../notifications/catalog';
import {
  useIsPro,
  useSubscriptionStore,
  type SubscriptionState,
  type SubscriptionStatus,
} from '../stores/subscriptionStore';
import type { NormalizedPackage } from '../services/purchasesService';
import { useApi } from '../api/client';
import {
  getPushDebugState,
  getPermissionStatus as getPushPermissionStatus,
  hasBannerPermission,
  isPushConfigured,
  requestPermission as requestPushPermission,
  type PushPermissionStatus,
} from '../services/pushService';
import { track } from '../services/analyticsService';
import { openFeedback } from '../utils/feedback';
import { friendlyErrorMessage } from '../utils/errorCopy';

interface PushPrefsResponse {
  ok: boolean;
  oneSignal?: 'ok' | 'failed';
  clerk?: 'ok' | 'failed';
}

const ACTIVE_GAME_BADGES: Record<GameId, { initial: string; bg: string; fg: string }> = {
  'mega-millions': { initial: 'M', bg: '#FFB800', fg: '#211600' },
  powerball: { initial: 'P', bg: '#E8375C', fg: '#FFFFFF' },
};

function activeGameSubtitle(game: GameDefinition): string {
  return `${game.mainCount} of ${game.mainRange} + ${game.bonusLabel}`;
}

/** "3 of 5" or "— of —" while loading. */
function formatScanUsage(
  used: number | null,
  cap: number | 'unlimited' | null
): string {
  const usedTxt = used ?? '—';
  const capTxt = cap === 'unlimited' ? '∞' : (cap ?? '—');
  return `${usedTxt} of ${capTxt}`;
}

/**
 * Store prices come from the RevenueCat offering, never from constants — a
 * hardcoded price drifts from App Store Connect and never localizes. When the
 * SDK can't reach the store we show a neutral placeholder rather than a number
 * we can't stand behind; the paywall surfaces the real failure.
 */
function planPrice(pkg: NormalizedPackage | null, status: SubscriptionStatus): string {
  if (pkg) return `${pkg.priceFormatted}${planPeriod(pkg.periodIso)}`;
  return status === 'loading' || status === 'unknown' ? 'Loading…' : 'View plans';
}

function planPeriod(iso: string | null): string {
  if (iso === 'P1M') return '/mo';
  if (iso === 'P1Y') return '/yr';
  if (iso === 'P1W') return '/wk';
  return '';
}

function planFootnote(offerings: SubscriptionState['offerings']): string {
  const monthly = offerings?.monthly ?? null;
  const annual = offerings?.annual ?? null;
  if (!monthly && !annual) return 'Subscriptions auto-renew until canceled.';
  const parts = [
    monthly ? `Chancey Pro Monthly is ${monthly.priceFormatted}${planPeriod(monthly.periodIso)}` : null,
    annual ? `Chancey Pro Annual is ${annual.priceFormatted}${planPeriod(annual.periodIso)}` : null,
  ].filter(Boolean);
  return `${parts.join('. ')}. Subscriptions auto-renew until canceled.`;
}

/**
 * Format the UTC reset timestamp in the user's local timezone. Always includes
 * a date component so "Thu 8:00 PM" style ambiguity (which Thursday?) can't
 * happen — near-term resets use a weekday + date, further out uses month-day
 * with optional year.
 */
function formatResetDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const msToReset = d.getTime() - now.getTime();
  if (msToReset <= 0) return 'Now';
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const sameCalendarYear = d.getFullYear() === now.getFullYear();
  // Include year only when it differs from today's — avoids "May 1, 2026"
  // clutter for the typical case but still clarifies edge-of-year resets.
  const shortDate = d.toLocaleDateString(
    undefined,
    sameCalendarYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
  );
  return `${shortDate} · ${time}`;
}

/**
 * Compact "PRO" chip. Used next to the user's name in Settings to give Pro
 * subscribers a visible marker of their status. Keep the visual simple: a
 * gradient pill that matches the paywall hero so the "earned" feeling carries
 * through.
 */
const ProBadge: React.FC<{
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
}> = ({ c, typ }) => (
  <span
    aria-label="Chancey Pro subscriber"
    className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white"
    style={{
      background: c.gradient,
      fontFamily: typ.body,
      letterSpacing: '0.1em',
    }}
  >
    Pro
  </span>
);

const ProfileAvatar: React.FC<{
  imageUrl?: string | null;
  fallback: string;
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
}> = ({ imageUrl, fallback, c, typ }) => {
  const initial = fallback.trim()[0]?.toUpperCase() || '?';
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full"
      style={{
        background: c.surfaceAlt,
        border: `0.5px solid ${c.line}`,
        color: c.ink,
        fontFamily: typ.body,
        fontWeight: 700,
      }}
      aria-hidden
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const { c, typ, d, resetOnboarding } = useChanceyTheme();
  const { user, isLoaded } = useUser();
  const clerk = useClerk();
  const { signOut } = clerk;
  const { prefs, setPartial } = useUserPrefs();
  const { gameId: activeGameId, setGameId } = useActiveGame();
  const history = useHistory();
  const { isAdmin } = useIsAdmin();
  const paywall = usePaywall();
  const api = useApi();
  const isPro = useIsPro();
  const managementUrl = useSubscriptionStore((s) => s.customerInfo?.managementUrl ?? null);
  const subStatus = useSubscriptionStore((s) => s.status);
  const refreshSub = useSubscriptionStore((s) => s.refresh);
  const restoreFn = useSubscriptionStore((s) => s.restore);
  const setServerQuota = useSubscriptionStore((s) => s.setServerQuota);
  const scansUsed = useSubscriptionStore((s) => s.scansUsedThisMonth);
  const scansCap = useSubscriptionStore((s) => s.scansCapThisMonth);
  const scansResetAt = useSubscriptionStore((s) => s.scansResetAtUtc);
  const offerings = useSubscriptionStore((s) => s.offerings);

  // Pull the latest scan quota from the server once this page mounts so the
  // Subscription section can show "N of 5 used" without the user needing to
  // actually scan first. Cheap (one KV read for free users, zero for Pro).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{
          pro: boolean;
          scans: { used: number; cap: number | 'unlimited'; resetsAtUtc: string };
        }>('/v1/me/subscription');
        if (cancelled) return;
        setServerQuota({
          used: res.scans.used,
          cap: res.scans.cap,
          resetAtUtc: res.scans.resetsAtUtc,
        });
      } catch {
        /* non-fatal: the row falls back to whatever the store already has */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, setServerQuota, isPro]);
  const [restoreState, setRestoreState] = useState<
    { kind: 'idle' | 'busy' } | { kind: 'done'; message: string }
  >({ kind: 'idle' });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [birthdayError, setBirthdayError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    field: 'name';
    label: string;
    value: string;
    multi: boolean;
  } | null>(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (!isLoaded || !user) return;
    const n =
      user.fullName ||
      [user.firstName, user.lastName].filter(Boolean).join(' ') ||
      user.username ||
      '';
    if (n) setName(n);
    const em = user.primaryEmailAddress?.emailAddress;
    if (em) setEmail(em);
  }, [isLoaded, user]);

  const startEditName = () => {
    setDraft(name);
    setSaveError(null);
    setEditing({ field: 'name', label: 'Display name', value: name, multi: false });
  };

  const closeEdit = () => {
    setEditing(null);
    setSaveError(null);
  };

  const saveDisplayName = async () => {
    if (!user) return;
    const trimmed = draft.trim();
    if (!trimmed) {
      setSaveError('Name cannot be empty.');
      return;
    }
    if (trimmed === name) {
      closeEdit();
      return;
    }
    const [first, ...rest] = trimmed.split(/\s+/);
    const last = rest.join(' ');
    setSaving(true);
    setSaveError(null);
    try {
      await user.update({ firstName: first, lastName: last });
      setName(trimmed);
      closeEdit();
    } catch (err) {
      const msg =
        friendlyErrorMessage(err, 'Could not update name. Please try again.');
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (deletingAccount) return;
    const confirmed = window.confirm('Are you sure you want to delete your account?');
    if (!confirmed) return;

    setDeletingAccount(true);
    setDeleteAccountError(null);
    try {
      await api.delete('/v1/me/account');
      await signOut().catch(() => undefined);
      history.replace('/');
    } catch (err) {
      const msg =
        friendlyErrorMessage(err, 'Could not delete account. Please try again.');
      setDeleteAccountError(msg);
    } finally {
      setDeletingAccount(false);
    }
  };

  const Toggle: React.FC<{ on: boolean; set: (v: boolean) => void }> = ({ on, set }) => (
    <button
      type="button"
      aria-pressed={on}
      className="relative h-[26px] w-11 shrink-0 rounded-[13px] transition-colors"
      style={{ background: on ? c.accent : c.line }}
      onClick={(e) => {
        e.stopPropagation();
        set(!on);
      }}
    >
      <span
        className="absolute top-0.5 h-[22px] w-[22px] rounded-full bg-white shadow transition-[left]"
        style={{ left: on ? 20 : 2 }}
      />
    </button>
  );

  const Section: React.FC<{ title: string; children: React.ReactNode; footnote?: string }> = ({
    title,
    children,
    footnote,
  }) => (
    <section aria-label={title}>
      <div
        className="px-[18px] pb-2 text-[11px] font-semibold uppercase tracking-[0.1em]"
        style={{ color: c.inkMuted, fontFamily: typ.body }}
      >
        {title}
      </div>
      <div
        className="overflow-hidden rounded-[18px] border"
        style={{ background: c.surface, borderColor: c.line }}
      >
        {children}
      </div>
      {footnote ? (
        <div
          className="mt-1.5 px-[18px] text-[11px] leading-snug"
          style={{ color: c.inkMuted, fontFamily: typ.body }}
        >
          {footnote}
        </div>
      ) : null}
    </section>
  );

  const Row: React.FC<{
    label: string;
    /** Small secondary text under the label (e.g. "Resets May 1"). */
    sublabel?: string;
    value?: string;
    onClick?: () => void;
    destructive?: boolean;
    last?: boolean;
    right?: React.ReactNode;
  }> = ({ label, sublabel, value, onClick, destructive, last, right }) => {
    const content = (
      <>
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-medium"
            style={{
              fontFamily: typ.body,
              color: destructive ? c.lucky : c.ink,
              fontWeight: destructive ? 600 : 500,
            }}
          >
            {label}
          </div>
          {sublabel ? (
            <div
              className="mt-0.5 text-[11px]"
              style={{ color: c.inkMuted, fontFamily: typ.body }}
            >
              {sublabel}
            </div>
          ) : null}
        </div>
        {right ||
          (value ? (
            <div
              className="max-w-[160px] truncate text-[13px]"
              style={{ color: c.inkSoft, fontFamily: typ.body }}
            >
              {value}
            </div>
          ) : null)}
        {onClick && !right ? Icon.arrow(14, c.inkMuted) : null}
      </>
    );
    const baseClass = 'flex w-full items-center gap-3 px-4 py-3.5 text-left';
    const baseStyle: React.CSSProperties = {
      borderBottom: last ? 'none' : `0.5px solid ${c.line}`,
    };
    if (onClick) {
      return (
        <button type="button" className={baseClass} style={baseStyle} onClick={onClick}>
          {content}
        </button>
      );
    }
    return (
      <div className={baseClass} style={baseStyle}>
        {content}
      </div>
    );
  };

  return (
    <IonPage>
      <IonContent fullscreen style={{ '--background': c.bg }}>
        <div className="flex flex-col gap-[18px]" style={safeAreaPadding(8, d.pad, 16, d.pad)}>
          <div className="px-0.5 pb-0.5 pt-1">
            <div
              className="text-xs font-medium uppercase tracking-[0.08em]"
              style={{ color: c.inkMuted, fontFamily: typ.body }}
            >
              Account
            </div>
            <div
              className="mt-0.5 text-[32px] leading-tight"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
                color: c.ink,
              }}
            >
              Settings
            </div>
          </div>

          <div
            className="flex items-center gap-3 rounded-[22px] border p-[14px]"
            style={{ background: c.surface, borderColor: c.line }}
          >
            <ProfileAvatar imageUrl={user?.imageUrl} fallback={name || email} c={c} typ={typ} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div
                  className="truncate text-[17px] leading-tight"
                  style={{
                    fontFamily: typ.display,
                    fontWeight: typ.displayWeight,
                    letterSpacing: typ.displayLetter,
                    color: c.ink,
                  }}
                >
                  {name || (isLoaded ? 'Your account' : '…')}
                </div>
                {isPro && <ProBadge c={c} typ={typ} />}
              </div>
              <div
                className="mt-0.5 truncate text-xs"
                style={{ color: c.inkSoft, fontFamily: typ.body }}
              >
                {email || '—'}
              </div>
            </div>
          </div>

          <Section title="Profile" footnote={birthdayError ?? undefined}>
            <Row label="Display name" value={name} onClick={startEditName} />
            <Row
              label="Birthday"
              value={formatBirthday(prefs.birthday)}
              last
              right={
                <input
                  type="date"
                  value={prefs.birthday || ''}
                  max={todayIso()}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (!raw) {
                      setPartial({ birthday: '' });
                      setBirthdayError(null);
                      return;
                    }
                    if (!isIsoDate(raw) || Date.parse(raw) > Date.now()) {
                      setBirthdayError('Please enter a valid date.');
                      return;
                    }
                    setBirthdayError(null);
                    setPartial({ birthday: raw, birthdayImported: true });
                  }}
                  className="w-[140px] rounded-lg border px-2 py-1 text-[13px] outline-none"
                  style={{
                    background: c.surfaceAlt,
                    borderColor: c.line,
                    color: c.ink,
                    fontFamily: typ.body,
                  }}
                />
              }
            />
          </Section>

          <Section title="Active game">
            <div className="px-4 py-3.5">
              <p className="mb-3 text-[13px] leading-relaxed" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                Picks, stats and rules will use this game&apos;s number ranges and draw schedule.
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {GAME_LIST.map((game) => {
                  const active = activeGameId === game.id;
                  const badge = ACTIVE_GAME_BADGES[game.id];
                  return (
                    <button
                      key={game.id}
                      type="button"
                      aria-pressed={active}
                      className="flex min-h-[96px] items-start gap-3 rounded-[14px] border-[1.5px] p-3 text-left transition-colors"
                      style={{
                        borderColor: active ? c.accent : c.line,
                        background: active ? c.surfaceAlt : 'transparent',
                      }}
                      onClick={() => setGameId(game.id)}
                    >
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[14px] font-bold"
                        style={{
                          background: badge.bg,
                          color: badge.fg,
                          fontFamily: typ.body,
                        }}
                        aria-hidden
                      >
                        {badge.initial}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className="block text-[16px] leading-tight"
                          style={{
                            color: c.ink,
                            fontFamily: typ.display,
                            fontWeight: typ.displayWeight,
                            letterSpacing: typ.displayLetter,
                          }}
                        >
                          {game.name}
                        </span>
                        <span className="mt-1 block text-[11px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                          {activeGameSubtitle(game)}
                        </span>
                      </span>
                      <span
                        className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2"
                        style={{
                          borderColor: active ? c.accent : c.line,
                          background: active ? c.accent : 'transparent',
                        }}
                        aria-hidden
                      >
                        {active ? (
                          <span className="block h-1.5 w-1.5 rounded-full bg-white" />
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>

          <Section title="Appearance">
            <Row
              label="Dark mode"
              right={<Toggle on={prefs.dark} set={(v) => setPartial({ dark: v })} />}
              last
            />
          </Section>

          <Section title="Experience">
            <div className="px-4 py-3.5">
              <p className="mb-3 text-[13px] leading-relaxed" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                Choose how much detail you want across{' '}
                <span style={{ color: c.ink, fontWeight: 600 }}>Stats</span> and the{' '}
                <span style={{ color: c.ink, fontWeight: 600 }}>Builder</span>. Both screens follow this setting.
              </p>
              <div className="flex gap-2">
                {(
                  [
                    {
                      id: 'simple' as const,
                      title: 'Simple',
                      desc: 'Glanceable insights & a few vibes.',
                    },
                    {
                      id: 'advanced' as const,
                      title: 'Advanced',
                      desc: 'Heatmaps, pairs, custom rule stacks.',
                    },
                  ] as const
                ).map((opt) => {
                  const active = prefs.experienceMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className="flex min-h-[88px] flex-1 flex-col gap-2 rounded-[14px] border-[1.5px] p-3 text-left transition-colors"
                      style={{
                        borderColor: active ? c.accent : c.line,
                        background: active ? c.surfaceAlt : 'transparent',
                      }}
                      onClick={() => setPartial({ experienceMode: opt.id })}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span
                          className="text-[16px] leading-tight"
                          style={{
                            fontFamily: typ.display,
                            fontWeight: typ.displayWeight,
                            letterSpacing: typ.displayLetter,
                            color: c.ink,
                          }}
                        >
                          {opt.title}
                        </span>
                        <span
                          className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2"
                          style={{
                            borderColor: active ? c.accent : c.line,
                            background: active ? c.accent : 'transparent',
                          }}
                          aria-hidden
                        >
                          {active ? (
                            <span className="block h-1.5 w-1.5 rounded-full bg-white" />
                          ) : null}
                        </span>
                      </div>
                      <p className="text-[11px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                        {opt.desc}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>

          <Section
            title="Subscription"
            footnote={
              restoreState.kind === 'done'
                ? restoreState.message
                : isPro
                  ? 'Change plan or cancel anytime. Changes take effect at the end of your current billing period.'
                  : planFootnote(offerings)
            }
          >
            {isPro ? (
              <>
                <Row
                  label="Chancey Pro"
                  value="Active"
                  right={<ProBadge c={c} typ={typ} />}
                />
                <Row
                  label="Ticket scans"
                  value="Unlimited"
                />
                <Row
                  label="Manage subscription"
                  value={
                    managementUrl
                      ? undefined
                      : subStatus === 'loading' || subStatus === 'unknown'
                        ? 'Loading…'
                        : 'Retry'
                  }
                  onClick={
                    managementUrl
                      ? () => {
                          try {
                            window.open(managementUrl, '_blank', 'noopener,noreferrer');
                          } catch {
                            /* best-effort */
                          }
                        }
                      : subStatus === 'error' || subStatus === 'ready'
                        // Status 'ready' but no URL = subscription came from a different
                        // platform (e.g. paid on iOS, viewing on web). Re-fetching won't
                        // help, but we re-run refresh as a best-effort retry path.
                        ? () => {
                            void refreshSub();
                          }
                        : undefined
                  }
                />
              </>
            ) : (
              <>
                <Row
                  label="Chancey Pro Monthly"
                  sublabel="Auto-renews every month"
                  value={planPrice(offerings?.monthly ?? null, subStatus)}
                  onClick={() => paywall.open({ reason: 'upgrade' })}
                />
                <Row
                  label="Chancey Pro Annual"
                  sublabel="Auto-renews every year"
                  value={planPrice(offerings?.annual ?? null, subStatus)}
                  onClick={() => paywall.open({ reason: 'upgrade' })}
                />
                <Row
                  label="Ticket scans this month"
                  sublabel={
                    scansResetAt
                      ? `Resets ${formatResetDate(scansResetAt)}`
                      : undefined
                  }
                  value={formatScanUsage(scansUsed, scansCap)}
                />
              </>
            )}
            <Row
              label={restoreState.kind === 'busy' ? 'Restoring…' : 'Restore purchases'}
              last
              onClick={
                restoreState.kind === 'busy'
                  ? undefined
                  : async () => {
                      setRestoreState({ kind: 'busy' });
                      try {
                        const info = await restoreFn();
                        setRestoreState({
                          kind: 'done',
                          message: info.isPro
                            ? 'Chancey Pro restored.'
                            : 'No active Chancey Pro subscription found for this account.',
                        });
                      } catch (err) {
                        const msg =
                          friendlyErrorMessage(err, 'Could not restore purchases.');
                        setRestoreState({ kind: 'done', message: msg });
                      }
                    }
              }
            />
          </Section>

          <NotificationsSection
            prefs={prefs}
            setPartial={setPartial}
            Section={Section}
            Row={Row}
            Toggle={Toggle}
          />


          {isAdmin && (
            <Section title="Admin">
              <Row
                label="Admin dashboard"
                onClick={() => history.push('/admin')}
                last
              />
            </Section>
          )}

          <Section title="Support">
            <Row label="Help center" onClick={() => history.push('/help')} />
            <Row label="Send feedback" onClick={() => openFeedback(user?.id)} />
            <Row label="See the intro again" onClick={() => resetOnboarding()} />
            <Row label="Terms of service" onClick={() => history.push('/terms')} />
            <Row label="Privacy policy" onClick={() => history.push('/privacy')} last />
          </Section>

          <Section title="Session">
            <Row label="Log out" onClick={() => void signOut()} last />
          </Section>

          <Section title="Danger zone" footnote={deleteAccountError ?? undefined}>
            <Row
              label={deletingAccount ? 'Deleting account…' : 'Delete account'}
              onClick={deletingAccount ? undefined : () => void deleteAccount()}
              destructive
              last
            />
          </Section>

          <div
            className="mt-1 text-center text-[11px]"
            style={{ color: c.inkMuted, fontFamily: typ.body }}
          >
            Chancey v{pkg.version}
          </div>
        </div>

        {editing && (
          <div
            className="fixed inset-0 z-[60] flex flex-col justify-end bg-black/45"
            role="presentation"
            onClick={closeEdit}
          >
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
              `}</style>
            <div
              className="rounded-t-[28px] px-[22px] pt-2.5"
              style={{
                ...safeAreaPaddingX(22),
                background: c.bg,
                animation: 'sheetUp 0.3s cubic-bezier(.2,.8,.2,1)',
                paddingBottom: `max(28px, ${withSafeArea('bottom')})`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center pb-4">
                <div className="h-[5px] w-10 rounded-md" style={{ background: c.line }} />
              </div>
              <div
                className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em]"
                style={{ color: c.inkMuted, fontFamily: typ.body }}
              >
                {editing.label}
              </div>
              <input
                autoFocus
                className="mb-2 w-full rounded-[14px] border px-4 py-3.5 text-[22px] outline-none"
                style={{
                  borderColor: c.accent,
                  background: c.surface,
                  color: c.ink,
                  fontFamily: typ.display,
                  fontWeight: typ.displayWeight,
                  letterSpacing: typ.displayLetter,
                }}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !saving) {
                    e.preventDefault();
                    void saveDisplayName();
                  }
                }}
              />
              {saveError ? (
                <div
                  className="mb-2 text-[12px]"
                  style={{ color: c.lucky, fontFamily: typ.body }}
                >
                  {saveError}
                </div>
              ) : null}
              <div className="mt-1 flex gap-2">
                <Btn
                  c={c}
                  typ={typ}
                  variant="ghost"
                  style={{ flex: 1 }}
                  onClick={closeEdit}
                  disabled={saving}
                >
                  Cancel
                </Btn>
                <Btn
                  c={c}
                  typ={typ}
                  variant="primary"
                  style={{ flex: 1.4 }}
                  onClick={() => void saveDisplayName()}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Btn>
              </div>
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

function isIsoDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear().toString().padStart(4, '0');
  const m = (now.getMonth() + 1).toString().padStart(2, '0');
  const d = now.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatBirthday(iso: string): string {
  if (!iso || !isIsoDate(iso)) return 'Not set';
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const monthName = months[m - 1] ?? '';
  return `${monthName} ${d}, ${y}`;
}

/**
 * Notifications section.
 *
 * Toggles are UI-writable (existing `useUserPrefs.setPartial` pattern), but
 * the authoritative delivery target lives in OneSignal tags written by the
 * server. Every toggle does two things:
 *   1. Optimistically flip the local pref (instant feedback, no latency)
 *   2. POST /v1/me/push/prefs so the server sets the matching OneSignal tag
 *
 * Drift between the two is self-healing: the next toggle re-tags both.
 *
 * Kept as a sub-component so the parent render stays uncluttered and the
 * permission probing doesn't re-run for unrelated renders.
 */
const NotificationsSection: React.FC<{
  prefs: ReturnType<typeof useUserPrefs>['prefs'];
  setPartial: ReturnType<typeof useUserPrefs>['setPartial'];
  Section: React.FC<{ title: string; children: React.ReactNode; footnote?: string }>;
  Row: React.FC<{
    label: string;
    sublabel?: string;
    value?: string;
    onClick?: () => void;
    destructive?: boolean;
    last?: boolean;
    right?: React.ReactNode;
  }>;
  Toggle: React.FC<{ on: boolean; set: (v: boolean) => void }>;
}> = ({ prefs, setPartial, Section, Row, Toggle }) => {
  const api = useApi();
  const [permission, setPermission] = useState<PushPermissionStatus>('unknown');
  const [syncState, setSyncState] = useState<
    { kind: 'idle' } | { kind: 'syncing' } | { kind: 'error'; msg: string }
  >({ kind: 'idle' });

  // Probe initial permission state so the "Enable in system settings" link
  // only shows when the OS actually has us in the Denied bucket.
  useEffect(() => {
    if (!isPushConfigured()) {
      setPermission('unknown');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getPushPermissionStatus();
        if (cancelled) return;
        setPermission(status);
      } catch {
        if (!cancelled) setPermission('unknown');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const syncToServer = async (patch: Partial<Record<NotificationPrefKey, boolean>>): Promise<void> => {
    if (!isPushConfigured()) return;
    setSyncState({ kind: 'syncing' });
    try {
      let subscriptionId: string | null | undefined;
      try {
        const pushDebug = await getPushDebugState();
        subscriptionId =
          pushDebug.subscriptionId &&
          pushDebug.subscriptionTokenPresent &&
          pushDebug.optedIn !== false
            ? pushDebug.subscriptionId
            : null;
      } catch {
        subscriptionId = null;
      }
      const res = await api.post<PushPrefsResponse>(
        '/v1/me/push/prefs',
        subscriptionId ? { ...patch, subscriptionId } : patch
      );
      if (!res.ok) {
        const failed = [
          res.oneSignal === 'failed' ? 'OneSignal' : null,
          res.clerk === 'failed' ? 'Clerk' : null,
        ].filter(Boolean).join(' + ') || 'server';
        setSyncState({ kind: 'error', msg: failed });
        return;
      }
      setSyncState({ kind: 'idle' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'sync error';
      setSyncState({ kind: 'error', msg });
    }
  };

  const onToggle = async (key: NotificationPrefKey, next: boolean): Promise<void> => {
    // Optimistic UI update first — the toggle feels instant even if the
    // server round-trip is slow.
    setPartial({ notif: { [key]: next } });
    track('notification_pref_change', { source: key, outcome: next ? 'on' : 'off' });

    // If the user flips a toggle ON and permission isn't granted, ask now.
    // Denial is a soft failure — the toggle stays "on" locally so the user's
    // intent is recorded, and the footer calls out the permission gap.
    if (next && !hasBannerPermission(permission) && isPushConfigured()) {
      try {
        const result = await requestPushPermission();
        setPermission(result.granted ? await getPushPermissionStatus() : 'denied');
      } catch {
        /* ignore; permission stays as-is */
      }
    }

    await syncToServer({ [key]: next });
  };

  const footnote = (() => {
    if (!isPushConfigured()) {
      return 'Push delivery is not configured on this build — your preferences are saved and will sync once it is.';
    }
    if (syncState.kind === 'error') {
      return `${syncState.msg} sync failed. Toggle again to retry.`;
    }
    if (permission === 'provisional') {
      return 'Chancey notifications are currently quiet-only. Turn a notification on to allow banners and sounds.';
    }
    if (permission === 'denied') {
      return 'Notifications are not enabled for Chancey. Enable them in device settings, then toggle again to sync.';
    }
    if (syncState.kind === 'syncing') {
      return 'Saving preference…';
    }
    return 'Changes sync to our server so you only get the alerts you want.';
  })();

  return (
    <Section title="Notifications" footnote={footnote}>
      {NOTIFICATION_PREF_ROWS.map((row, i) => (
        <Row
          key={row.key}
          label={row.label}
          right={
            <Toggle
              on={prefs.notif[row.key]}
              set={(v) => void onToggle(row.key, v)}
            />
          }
          last={i === NOTIFICATION_PREF_ROWS.length - 1}
        />
      ))}
    </Section>
  );
};

export default SettingsPage;
